<template>
    <div class="md-layout">
        <div
            class="md-layout-item md-size-100 delivery-window-override-day-container"
            v-if="getOverrideDayName(serviceAreaItem)"
        >
            <div class="override-day-content">
                <div class="override-day-title">{{ getOverrideDayName(serviceAreaItem) }}</div>
                <div class="override-day-description">
                    Special capacity settings applied for {{ formatDate(selectedDate) }}
                </div>
            </div>
            <div class="override-day-date">{{ formatDate(selectedDate) }}</div>
        </div>
        <div
            v-for="(capacityItems, itemRange) in getGroupedDeliveryWindows(serviceAreaItem)"
            class="md-layout-item md-size-100 delivery-window-container"
            :key="itemRange"
        >
            <template v-if="itemRange === '12:00 AM - 11:59 PM'">
                <div class="delivery-window-header">
                    <h6 class="delivery-window-title">No time window</h6>
                </div>
                <div class="capacity-container">
                    <div class="capacity-tile" v-for="(capacityItem, index) in capacityItems" :key="index">                        
                        <stats-card :show-actions="false">
                            <template slot="header">
                                <p class="category">
                                    {{ getCapacityItemTypeLabel(capacityItem.type) }}
                                </p>
                                <h3 class="title">
                                    {{ getCurrentCapacityWithSymbol(capacityItem) }}
                                </h3>
                            </template>
                        </stats-card>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="delivery-window-header">
                    <h6 class="delivery-window-title">{{ itemRange }}</h6>
                </div>
                <div class="capacity-container">
                    <div class="capacity-item-container" v-for="(capacityItem, index) in capacityItems" :key="index">
                        <div class="capacity-item-title">
                            {{ getCapacityItemTypeLabel(capacityItem.type) }}
                        </div>
                        <div class="capacity-item-bar-container">
                            <div
                                class="capacity-item-bar-container-fill"
                                :style="{
                                    width: hasMaxCapacity(capacityItem) ? getPercentage(capacityItem) + '%' : '100%',
                                    backgroundColor: getCapacityBarColor(capacityItem)
                                }"
                            ></div>
                        </div>
                        <div class="capacity-item-info">
                            {{ getCapacityInfoLabel(capacityItem) }}
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE, DATE_TYPES, CAPACITY_TYPES_KEY_CONSTANTS } from '@/utils/constants';
import { StatsCard } from '@/components';

export default {
    name: 'ServiceAreasDashboardCapacityComponent',
    components: {
        StatsCard
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    data() {
        return {
            loadCapacityList: []
        }
    },
    props: {
        selectedDate: {
            type: String,
            default: ''
        },
        serviceAreaItem: {
            type: Object,
            required: true
        }
    },
    mounted() {
        this.loadCapacityList = this.user.vehicleCapacityUnitsConfiguration || [];
    },
    methods: {
        getOverrideDayName(serviceAreaItem) {
            return serviceAreaItem?.capacityDetails?.overrideDayName;
        },
        getGroupedDeliveryWindows(serviceAreaItem) {
            const items = serviceAreaItem?.capacityDetails?.deliveryWindowCapacityMetrics ?? [];

            return items.reduce((groups, item) => {
                const startTimeInUserLocale = this.$options.filters.timeFormat(
                    item.startTime,
                    DATE_TYPES.standardTime,
                    true
                );
                const endTimeInUserLocale = this.$options.filters.timeFormat(
                    item.endTime,
                    DATE_TYPES.standardTime,
                    true
                );

                const key = `${startTimeInUserLocale} - ${endTimeInUserLocale}`;
                if (!groups[key]) {
                    groups[key] = [];
                }
                groups[key].push(item);
                return groups;
            }, {});
        },
        hasMaxCapacity(capacityItem) {
            return capacityItem.maxCapacity && capacityItem.maxCapacity > 0;
        },
        getPercentage(capacityItem) {
            if (!this.hasMaxCapacity(capacityItem)) {
                return 0;
            }
            return Math.round((capacityItem.currentCapacity / capacityItem.maxCapacity) * 100);
        },
        getCapacityItemTypeLabel(type) {
            switch (type) {
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Stops:
                    return 'Stops';
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Volume:
                    return 'Volume';
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Weight:
                    return 'Weight';
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Quantity:
                    return 'Quantity';
                default:
                    return 'Unknown';
            }
        },
        getCurrentCapacityWithSymbol(capacityItem) {
            let symbol = '';

            switch (capacityItem.type) {
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Volume:
                    symbol = this.loadCapacityList.find((c) => c.type === CAPACITY_TYPES_KEY_CONSTANTS.volume)?.symbol;
                    break;
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Weight:
                    symbol = this.loadCapacityList.find((c) => c.type === CAPACITY_TYPES_KEY_CONSTANTS.weight)?.symbol;
                    break;
                case SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Quantity:
                    symbol = this.loadCapacityList.find((c) => c.type === CAPACITY_TYPES_KEY_CONSTANTS.quantity)?.symbol;
                    break;
                default:
                    symbol = '';
            }

            return `${capacityItem.currentCapacity ?? 0} ${symbol ?? ''}`;
        },
        getCapacityInfoLabel(capacityItem) {
            if (!this.hasMaxCapacity(capacityItem)) {
                return `${capacityItem.currentCapacity ?? 0}/X`;
            }
            let label = '';
            if (capacityItem.type === SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Stops) {
                label = ' stops';
            }
            if (capacityItem.type === SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Volume) {
                label = ' m³';
            }
            if (capacityItem.type === SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Weight) {
                label = ' kg';
            }
            if (capacityItem.type === SERVICE_AREA_CAPACITY_STATUS_ITEM_TYPE.Quantity) {
                label = ' items';
            }
            return `${this.getPercentage(capacityItem)}% (${capacityItem.currentCapacity ?? 0}/${
                capacityItem.maxCapacity
            }${label})`;
        },
        formatDate(dateString) {
            if (dateString === undefined || dateString === null || dateString === '') {
                return '';
            }

            return moment(dateString).format('MMMM DD, yyyy');
        },
        getCapacityBarColor(capacityItem) {            
            if (!this.hasMaxCapacity(capacityItem)) {
                return '#334E69'; // blue
            }
            const ratio = (capacityItem.currentCapacity / capacityItem.maxCapacity) * 100;
            if (ratio >= 90) {
                return '#eb4846'; // red
            }
            if (ratio >= 70) {
                return '#fb920d'; // orange
            }
            return '#4caf50'; // green
        }
    }
};
</script>

<style lang="scss" scoped>
.delivery-window-override-day-container {
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: rgba(43, 147, 255, 0.16);
    margin-top: -9px;
    padding-top: 9px;
}

.override-day-date,
.override-day-title,
.delivery-window-title,
.capacity-item-title {
    font-weight: 600;
}

.override-day-title {
    color: #384553;
    margin-bottom: 2px;
}

.override-day-description {
    font-size: 13px;
    color: #666;
}

.delivery-window-container {
    padding: 10px;
}

.delivery-window-container:not(:last-child) {
    border-bottom: 1px solid #eee;
}

.delivery-window-header,
.capacity-container {
    display: flex;
    margin-bottom: 10px;
    margin-left: 5px;
    margin-right: 5px;
}

.delivery-window-header {
    justify-content: space-between;
    align-items: center;
}

.delivery-window-title {
    font-size: 14px;
    margin: 0;
}

.capacity-container {
    flex-wrap: nowrap;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 5px;
}

.capacity-item-container {
    flex: 1;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    background-color: #f5f5f5;
    padding: 8px;
}

.capacity-item-container,
.capacity-item-bar-container {
    border-radius: 4px;
}

.capacity-item-title {
    margin-bottom: 3px;
}

.capacity-item-info {
    font-size: 11px;
    color: #666;
    margin-top: 5px;
}

.capacity-item-bar-container {
    height: 16px;
    background-color: #e0e0e0;
    overflow: hidden;
    margin-bottom: 3px;
}

.capacity-item-bar-container-fill {
    height: 100%;
}

.capacity-tile {
    flex: 1;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    background-color: #f5f5f5;
    border-radius: 4px;
}

.md-card.md-card-stats {
    margin-top: 5px;
    margin-bottom: 5px;
    padding-top: 8px;
    padding-bottom: 8px;
    box-shadow: none;
    background-color: #f5f5f5;
}
</style>
